<template>
  <div>
    <product-image-carousel
      ref="imageCarousel"
      :breakpoints="{xs: 1}"
      :ratio="1.1"
      page-indicators="inset"
      inset-navigation
      :loop="theme().get('productPage.imageCarousel.loop') === '1'"
      @slide-change="onSlideChange"
    >
      <template #default>
        <div>
          <template v-if="showPersonalizationPreview">
            <tml-ratio
              v-for="layer in personalizationPreviewLayers"
              :key="`personalization-${layer}`"
              :ratio="1"
            >
              <tml-image-zoom :img-normal="getZoomImage(layer)">
                <template #default>
                  <personalization-canvas
                    :layer="layer"
                    :layers="personalizationPreviewLayers.map(tag => ({tag}))"
                    @initialised="
                      $emit('canvas-initialised', {layer, canvas: $event})
                    "
                    @generated-thumbnail="onGeneratedThumbnail(layer, $event)"
                    @generated-zoom-image="onGeneratedZoomImage(layer, $event)"
                  />
                </template>
              </tml-image-zoom>
            </tml-ratio>
          </template>
          <tml-ratio v-for="(video, index) in videos" :key="index" :ratio="1">
            <tml-video
              :video="video.url"
              :ratio="1"
              :video-thumbnail="video.thumbnails['1080']"
            />
          </tml-ratio>
          <tml-ratio
            v-for="(image, index) in images(1080)"
            :key="index"
            :ratio="1"
          >
            <template v-if="viewportSize.isSmaller('md')">
              <tml-image
                :alt="getAltText(image)"
                :src="image.url"
                class="w-full"
                :lazy-load="index !== 0"
                :ratio="1"
                :src-set="[
                  {width: 640, height: 640},
                  {width: 1080, height: 1080},
                ]"
              >
                <div
                  v-if="showSaleIndicators"
                  class="sale-badge thumb-size mb-4 rounded-full float-right m-4"
                >
                  <h3 class="text-white bold h-full">Sale</h3>
                </div>
              </tml-image>
            </template>
            <tml-image-zoom
              v-else
              class="w-full h-full"
              :img-normal="image.url"
              :img-zoom="
                splitTests.isActiveVariation('product_page_refresh', 'after')
                  ? image.srcUrl
                  : image.url
              "
              :alt="getAltText(image)"
              :lazy="index !== 0"
            />
          </tml-ratio>
        </div>
      </template>
    </product-image-carousel>
    <div
      v-if="enablePersonalization && disablePersonalizationMockups"
      class="absolute h-0 w-0 overflow-hidden"
      aria-hidden="true"
    >
      <personalization-canvas
        v-for="layer in personalizationPreviewLayers"
        :key="`personalization-${layer}`"
        :layer="layer"
        :layers="personalizationPreviewLayers.map(tag => ({tag}))"
        @initialised="$emit('canvas-initialised', {layer, canvas: $event})"
      />
    </div>
  </div>

  <tml-carousel
    v-if="
      !viewportSize.isSmaller('md') &&
      videos.concat(images(120)).length > 1 &&
      theme().get('productPage.thumbnails.layout') === 'carousel'
    "
    ref="thumbnailCarousel"
    class="mt-4"
    :breakpoints="{xs: 8}"
    :ratio="1"
    :spacing="8"
    :active-item-override="currentThumbnailIndex"
    :overlap="40"
    :make-active-item-first="makeActiveItemFirst"
    icon-size="1.25em"
  >
    <template #default="{getRatioByIndex}">
      <template
        v-for="(item, index) in videos
          .map(video => ({
            type: 'video',
            index: video.index,
            video: video,
          }))
          .concat(
            images(120).map((image, index) => ({
              type: 'image',
              index: index,
              image: image,
            }))
          )"
        :key="`${item.type}-${item.index}`"
      >
        <tml-image
          v-if="item.type === 'video'"
          class="cursor-pointer"
          :alt="item.video.altText"
          :src="item.video.thumbnails['120']"
          :ratio="getRatioByIndex(index)"
          @click="onClickThumb(item.index + personalizationImageCount)"
        >
          <div class="flex grow justify-center items-center h-full">
            <font-awesome-icon :icon="faPlay" class="text-white h-6" />
          </div>
        </tml-image>
        <tml-image
          v-else-if="item.type === 'image'"
          class="cursor-pointer"
          :alt="getAltText(item.image)"
          :src="item.image.url"
          :ratio="getRatioByIndex(index)"
          @click="onClickThumb(item.image.index)"
        />
      </template>
    </template>
  </tml-carousel>
  <tml-grid
    v-else-if="
      !viewportSize.isSmaller('md') &&
      (images(120).length > 1 || enablePersonalization)
    "
    class="mt-4"
    :breakpoints="{xs: 8}"
    :spacing="{xs: '0.5em', lg: '1em'}"
  >
    <tml-image
      v-for="(video, index) in videos"
      :key="index"
      class="cursor-pointer"
      :alt="video.altText"
      :src="video.thumbnails['120']"
      @click="onClickThumb(index + personalizationImageCount)"
    >
      <div class="flex grow justify-center items-center h-full">
        <font-awesome-icon :icon="faPlay" class="text-white h-6" />
      </div>
    </tml-image>
    <tml-image
      v-for="(image, index) in images(120)"
      :key="index"
      class="cursor-pointer"
      :alt="getAltText(image)"
      :src="image.url"
      @click="onClickThumb(image.index)"
    />
  </tml-grid>
</template>

<script>
import {faChevronUp} from '@fortawesome/pro-light-svg-icons/faChevronUp';
import {faChevronDown} from '@fortawesome/pro-light-svg-icons/faChevronDown';
import {faPlay} from '@fortawesome/pro-solid-svg-icons/faPlay';

import {clamp} from 'lodash';
import {
  formatUrl,
  viewportSize,
  image as imageUrl,
} from '@teemill/common/helpers';

import ProductImageCarousel from './ProductImageCarousel.vue';
import {defineAsyncComponent} from 'vue';
import {
  TmlSplitTest,
  TmlSplitTestVariation,
  TmlCarousel,
} from '@teemill/components';
import {splitTests} from '@teemill/common/classes';

export default {
  name: 'ProductPreview',

  components: {
    ProductImageCarousel,
    TmlCarousel,
    PersonalizationCanvas: defineAsyncComponent(() =>
      import('./PersonalizationCanvas.vue')
    ),
  },

  inject: ['theme'],

  props: {
    product: Object,

    selectedColor: String,

    enablePersonalization: Boolean,
    disablePersonalizationMockups: {
      type: Boolean,
      default: false,
    },
    requiredPersonalizationLayers: {
      type: Array,
      default: () => ['front'],
    },
    personalizationLayer: String,
  },

  data() {
    return {
      faChevronUp,
      faChevronDown,
      faPlay,

      viewportSize,

      thumbsOffset: 0,
      thumbOffsetInterval: 0,
      generatedThumbnails: {},
      generatedZoomImages: {},

      splitTests,

      lastClickedImageIndex: null,

      currentThumbnailIndex: 0,
      makeActiveItemFirst: false,
    };
  },

  computed: {
    personalizationPreviewLayers() {
      const layers = this.requiredPersonalizationLayers?.filter(
        layer => typeof layer === 'string' && layer.length > 0
      );

      return layers?.length ? layers : ['front'];
    },

    showSaleIndicators() {
      if (this.product && this.product.salePrice) {
        return (
          this.product.salePrice < this.product.price &&
          this.product.showSaleBadge
        );
      }

      return false;
    },

    videos() {
      return (
        this.product?.videos?.map(video => ({
          url: video.url,
          altText: video.altText,
          thumbnails: video.thumbnails,
        })) || []
      );
    },

    personalizationImageCount() {
      return this.showPersonalizationPreview
        ? this.personalizationPreviewLayers.length
        : 0;
    },

    showPersonalizationPreview() {
      return this.enablePersonalization && !this.disablePersonalizationMockups;
    },
  },

  watch: {
    selectedColor: {
      handler(value, oldValue) {
        if (!this.$refs.imageCarousel) {
          return;
        }

        if (
          this.showPersonalizationPreview &&
          this.currentThumbnailIndex >= this.personalizationImageCount
        ) {
          this.$refs.imageCarousel.goToPage(0);

          this.makeActiveItemFirst = true;
          this.currentThumbnailIndex = 0;
          return;
        }

        const isFirstTimeSelectingColor = oldValue === null;

        //Skip if we're on the product page and the color is not set in the URL
        if (isFirstTimeSelectingColor && !this.$route.params.colour) return;

        const hasLifestyleImages = this.product.lifestyleImages.length > 0;
        const images = this.images(1080);

        let preferredIndex = null;
        if (
          this.theme().get('productPage.imageCarousel.update.form') === '1' &&
          this.lastClickedImageIndex !== null
        ) {
          const img = images[this.lastClickedImageIndex];
          if (
            img &&
            ((hasLifestyleImages && img?.color === value) ||
              img?.optionId === value)
          ) {
            preferredIndex = this.lastClickedImageIndex;
          }
          this.lastClickedImageIndex = null;
        }

        const matchedImageIndex = images.findIndex(
          image =>
            (hasLifestyleImages && image?.color === value) ||
            image?.optionId === value
        );

        const targetIndex = preferredIndex ?? matchedImageIndex;

        const currentImage = images[this.$refs.imageCarousel.activeSlide];
        const currentImageMatches =
          currentImage &&
          ((hasLifestyleImages && currentImage?.color === value) ||
            currentImage?.optionId === value);

        if (
          targetIndex !== -1 &&
          targetIndex !== null &&
          !currentImageMatches
        ) {
          const carouselPage =
            this.personalizationImageCount + this.videos.length + targetIndex;

          this.$refs.imageCarousel.goToPage(carouselPage);

          this.makeActiveItemFirst = true;
          this.currentThumbnailIndex = carouselPage;
        }
      },
      immediate: true,
    },

    personalizationLayer(layer) {
      this.showPersonalizationLayer(layer);
    },
  },

  methods: {
    formatUrl,

    showPersonalizationLayer(layer) {
      if (
        !layer ||
        !this.$refs.imageCarousel ||
        !this.showPersonalizationPreview
      ) {
        return;
      }

      const layerIndex = this.personalizationPreviewLayers.indexOf(layer);

      if (
        layerIndex !== -1 &&
        this.$refs.imageCarousel.activeSlide !== layerIndex
      ) {
        this.$refs.imageCarousel.goToPage(layerIndex);
      }
    },

    /**
     * Gets the alt text of an image. Provides generic alt text if none found.
     *
     * @param { {url:string; optionId:string; altText?:string;} } image Image
     *
     * @return { string } Image alt text
     */
    getAltText(image) {
      if (image.altText) {
        return image.altText;
      }

      return `${
        image.optionId
          ? `${image.optionId} ${this.product.name}`
          : this.product.name
      }`;
    },

    nonExplodableImages(size) {
      return this.images(size).filter(image => !image.explode);
    },

    explodableImages(size) {
      return this.images(size).filter(image => image.explode);
    },

    explodableImageRows(size) {
      const images = this.explodableImages(size);

      const rows = [];
      let row = [];
      let rowSize = 3;

      images.forEach((image, index) => {
        if (row.length === rowSize) {
          rows.push(row);
          row = [];
          rowSize = rowSize === 3 ? 2 : 3;
        }

        row.push(image);

        if (index === images.length - 1) {
          rows.push(row);
        }
      });

      return rows;
    },

    images(size) {
      const lifestyleImages = this.product.lifestyleImages
        .filter(image => image[size])
        .map(image => ({
          url: imageUrl(formatUrl(image[size]), size, size),
          srcUrl: formatUrl(image['full']),
          altText: image?.altText,
          color: image?.color,
          explode: true,
        }));

      const flatImages = [];

      Object.values(this.product.options).forEach(option => {
        if (option.flatImage) {
          flatImages.push({
            optionId: option.colour,
            url: formatUrl(option.flatImage[size]),
            srcUrl: formatUrl(option.flatImage['full']),
            explode: false,
          });
        }

        if (option.backImage) {
          flatImages.push({
            optionId: option.colour,
            url: formatUrl(option.backFlatImage[size]),
            srcUrl: formatUrl(option.backFlatImage['full']),
            explode: false,
          });
        }

        if (option.additionalImages) {
          option.additionalImages.forEach((image, index) => {
            flatImages.push({
              optionId: option.colour,
              url: formatUrl(image[size]),
              srcUrl: formatUrl(image['full']),
              explode: false,
            });
          });
        }
      });

      const images =
        this.enablePersonalization && !this.disablePersonalizationMockups
          ? lifestyleImages
          : [...lifestyleImages, ...flatImages];

      if (
        this.showPersonalizationPreview &&
        this.generatedThumbnails &&
        size < 1080
      ) {
        const personalizationThumbnails = this.personalizationPreviewLayers
          .map((layer, index) => ({
            layer,
            index,
            src: this.generatedThumbnails[layer],
          }))
          .filter(({src}) => !!src)
          .map(({src, index}) => ({
            url: src,
            altText: 'Preview',
            color: this.selectedColor,
            explode: false,
            index,
          }));
        images.unshift(...personalizationThumbnails);
      }

      return images.map((image, index) => ({
        index: index + this.videos.length,
        ...image,
      }));
    },

    onClickThumb(index) {
      this.lastClickedImageIndex = this.getImageIndexFromCarousel(index);
      if (this.$refs.imageCarousel) {
        this.$refs.imageCarousel.goToPage(index);
      }
      if (this.theme().get('productPage.imageCarousel.update.form') === '1') {
        this.emitColorSelection(index);
      }

      this.makeActiveItemFirst = false;
      this.currentThumbnailIndex = index;
    },
    onSlideChange(index) {
      if (this.theme().get('productPage.imageCarousel.update.form') === '1') {
        this.emitColorSelection(index);
      }

      this.makeActiveItemFirst = false;
      this.currentThumbnailIndex = index;
    },

    emitColorSelection(index) {
      const images = this.images(1080);
      const imageIndex = this.getImageIndexFromCarousel(index);
      if (imageIndex === null) {
        return;
      }

      const selectedImage = images[imageIndex];

      if (!selectedImage) {
        return;
      }

      if (selectedImage.color) {
        this.$emit('color-selected', selectedImage.color);
      } else if (selectedImage.optionId) {
        this.$emit('color-selected', selectedImage.optionId);
      }
    },

    onMouseEnterThumbNav(direction) {
      if (this.$refs.thumbListContainer && this.$refs.thumbsList) {
        const thumbsContainerHeight =
          this.$refs.thumbListContainer.clientHeight;
        const thumbsHeight = this.$refs.thumbsList.clientHeight;

        if (this.thumbOffsetInterval === 0) {
          this.thumbOffsetInterval = setInterval(() => {
            if (direction === 'up') {
              this.thumbsOffset += 2;
            } else if (direction === 'down') {
              this.thumbsOffset -= 2;
            }

            this.thumbsOffset = clamp(
              this.thumbsOffset,
              thumbsContainerHeight - thumbsHeight,
              0
            );
          }, 10);
        }
      }
    },

    onMouseLeaveThumbNav() {
      clearInterval(this.thumbOffsetInterval);
      this.thumbOffsetInterval = 0;
    },

    onGeneratedThumbnail(layer, src) {
      this.generatedThumbnails[layer] = src;
    },

    onGeneratedZoomImage(layer, src) {
      this.generatedZoomImages[layer] = src;
    },

    getZoomImage(layer) {
      return this.generatedZoomImages[layer] || '';
    },

    getImageIndexFromCarousel(index) {
      const personalizationCount = this.personalizationImageCount;
      const videoCount = this.videos.length;

      const indexAfterPersonalization = index - personalizationCount;
      if (indexAfterPersonalization < 0) {
        return null;
      }

      if (indexAfterPersonalization < videoCount) {
        return null;
      }

      return indexAfterPersonalization - videoCount;
    },
  },
};
</script>

<style lang="scss" scoped>
.sale-badge {
  height: 80px;
  width: 80px;
  background-color: $discounted-color !important;
  h3 {
    color: $white-text;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 2px;
  }
}
</style>
